import ClassNames from 'classnames';
import { isString, get, uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';

import { SIZES } from './constants';

export { SIZES };

const Checkbox = class extends React.Component {
  constructor(props) {
    super(props);
    this.id = uniqueId('allbirds-checkbox-');
  }

  render() {
    const { isChecked, ariaLabel, className, children, size, isInverted, ...otherProps } =
      this.props;

    return (
      <label
        className={ClassNames(
          'Checkbox',
          `Checkbox--${size}`,
          { 'Checkbox--inverted': isInverted },
          className
        )}
        htmlFor={this.id}
      >
        <input
          id={this.id}
          type="checkbox"
          className="Checkbox__input"
          checked={isChecked}
          aria-label={ariaLabel}
          {...otherProps}
        />
        <span className="Checkbox__checkmark" />
        {children && <span className="Checkbox__label">{children}</span>}
        <style jsx>
          {`
            .Checkbox {
              cursor: pointer;
              display: flex;
              align-items: center;
            }

            .Checkbox__label {
              margin-left: 6px;
              font-weight: ${Theme.get('font-weight-normal')};
              font-size: 14px;
            }

            .Checkbox--small .Checkbox__checkmark {
              width: 16px;
              height: 16px;
              border-width: 1px;
            }

            .Checkbox--large .Checkbox__checkmark {
              width: 24px;
              height: 24px;
              border-width: 3px;
            }

            .Checkbox__checkmark {
              display: inline-block;
              flex-shrink: 0;
              border: solid 3px ${Theme.get('colors-white')};
              border-radius: ${Theme.get('border-radius-px')};
              background-color: transparent;
              position: relative;
              box-shadow: 0 0 0 1px ${Theme.get('colors-black')};
              transition: ${Theme.get('transition-duration-fast-ms')}ms background-color
                ${Theme.get('transition-timing-ease-out')};
            }

            .Checkbox__checkmark:before {
              display: block;
              content: '';
              border: solid ${Theme.get('colors-white')};
              border-width: 0 1px 1px 0;
              transform: rotate(45deg);
              margin: 0 auto;
            }

            .Checkbox--small .Checkbox__checkmark:before {
              width: 5px;
              height: 10px;
            }

            .Checkbox--large .Checkbox__checkmark:before {
              width: 7px;
              height: 12px;
            }

            .Checkbox__input {
              position: absolute;
              opacity: 0;
            }

            .Checkbox--inverted .Checkbox__checkmark {
              border: solid 1px ${Theme.get('colors-white')};
              color: ${Theme.get('colors-white')};
            }

            .Checkbox--inverted .Checkbox__checkmark:before {
              display: block;
              content: '';
              border: solid ${Theme.get('colors-black')};
              border-width: 0 1px 1px 0;
              transform: rotate(45deg);
              margin: 0 auto;
            }

            @media ${largeUp} {
              .Checkbox:hover .Checkbox__input ~ .Checkbox__checkmark {
                background-color: ${Theme.get('colors-black-20')};
              }
            }

            .Checkbox input.focused ~ .Checkbox__checkmark {
              outline: 2px solid ${Theme.get('colors-black')};
            }

            .Checkbox .Checkbox__input:checked ~ .Checkbox__checkmark {
              background-color: ${Theme.get('colors-black')};
            }

            .Checkbox--inverted input.focused ~ .Checkbox__checkmark {
              outline: 2px solid ${Theme.get('colors-white')};
            }

            .Checkbox--inverted .Checkbox__input:checked ~ .Checkbox__checkmark {
              background-color: ${Theme.get('colors-white')};
            }
          `}
        </style>
      </label>
    );
  }
};

Checkbox.propTypes = {
  isChecked: PropTypes.bool,
  ariaLabel: props => {
    const ariaLabel = get(props, 'ariaLabel');
    const children = get(props, 'children');

    if (!children && !ariaLabel) {
      return new Error(`Either "children" or "ariabLabel" props must have a value.`);
    }

    if (ariaLabel && !isString(ariaLabel)) {
      return new Error(`Prop "ariaLabel" must be a string`);
    }

    return null;
  },
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  size: PropTypes.oneOf(Object.values(SIZES)),
  isInverted: PropTypes.bool,
};

Checkbox.defaultProps = {
  isChecked: false,
  className: null,
  children: null,
  ariaLabel: null,
  size: SIZES.LARGE,
  isInverted: false,
};

export default Checkbox;
